<template>
  <div class="border-b px-4 py-8 md:px-8">
    <p class="text-[16px] md:text-[20px]">3. Select Number of participants</p>
    <p v-if="isParticipantPickerVisible" class="mt-2 text-sm font-normal leading-tight text-gray-500" data-testid="capacity-notice">{{ CAPACITY_NOTICE }}</p>
    <div v-if="props.experienceEvent.timeSlotId">
      <div v-for="fee in props.experienceEvent.experienceEventFees" :key="fee.guestType.id">
        <NumberOfPeoplePerGuestTypePicker
          :count="guestCounts[fee.guestType.id] || 0"
          :disabled="!isSelectedDate || reservationStep1Form.experienceEventId === ''"
          :error="getErrorMessage(`step1.guestCounts.${fee.guestType.id}`)"
          :experience-event-fee="fee"
          :guest-type="fee.guestType"
          :max-count="calculateMaxCountForGuestType(fee.guestType)"
          :on-decrement="() => decrementGuestCount(fee.guestType.id)"
          :on-increment="() => incrementGuestCount(fee.guestType.id)"
        />
      </div>
    </div>
    <div v-else class="ml-1 pt-2">
      <WrappedSelectBox
        v-if="isSelectedDate"
        v-model="form.step1.numOfParticipants"
        aria-label="numOfParticipantsSelects"
        label=""
        lang="en"
        :default-value="defaultParticipants"
        :disabled="reservationStep1Form.experienceEventId === ''"
        :error="errorMessage"
        :options="participantsOptions"
        :required="true"
      />
      <p v-else class="ml-5 text-gray2" data-testid="no-selected-date-message-in-number-of-participant">Please select a date first</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type Options } from '@/models/option';
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { WrappedSelectBox } from '@/wabunka-src/common/components';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';
import type { WabunkaReservationFormStep1 } from '@/wabunka-src/features/Experience';
import type { WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import type { WabunkaGuestTypeViewModel } from '@/wabunka-src/features/Experience/models/guestType';
import NumberOfPeoplePerGuestTypePicker from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/ReservationStep1Form/NumberOfParticipantPicker/NumberOfPeoplePerGuestTypePicker/NumberOfPeoplePerGuestTypePicker.vue';
import { CAPACITY_NOTICE } from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/ReservationStep1Form/NumberOfParticipantPicker/capacityNotice';

interface Props {
  experience: WabunkaExperienceViewModel;
  experienceEvent: WabunkaExperienceEventViewModel;
  getErrorMessage: (s: string) => string;
  reservationStep1Form: WabunkaReservationFormStep1;
  participantsOptions: Options;
  defaultParticipants: number;
  isSelectedDate: boolean;
}

const props = defineProps<Props>();
// 本当はpropsの値を使いたいが、selecteBoxはv-modelを使っていてpropsの直接参照ができない。そのため仕方なくcomposablesを呼び出している...
const { form } = useGlobalWabunkaReservationForm();

// ゲストタイプごとの人数を管理（初期値を含む）
const guestCounts = computed(() => {
  const counts: Record<string, number> = {};

  // カレンダーが選択されていない、またはtimeSlotIdがない場合は全て0
  if (!props.isSelectedDate || !props.experienceEvent.timeSlotId) {
    props.experienceEvent.experienceEventFees?.forEach((fee) => {
      counts[fee.guestType.id] = form.value.step1.guestCounts?.[fee.guestType.id] || 0;
    });
    return counts;
  }

  props.experienceEvent.experienceEventFees?.forEach((fee) => {
    const existingCount = form.value.step1.guestCounts?.[fee.guestType.id];
    counts[fee.guestType.id] = existingCount || 0;
  });

  return counts;
});

const errorMessage = computed(() => {
  return props.getErrorMessage('step1.numOfParticipants');
});

// ゲストタイプカードまたはプルダウンが出ているとき＝最大人数が画面に出ているときだけ案内する
const isParticipantPickerVisible = computed(() => Boolean(props.experienceEvent.timeSlotId) || props.isSelectedDate);

// WabunkaReservationFormStep1 型を拡張して guestCounts プロパティを追加
declare module '@/wabunka-src/features/Experience' {
  interface WabunkaReservationFormStep1 {
    guestCounts?: Record<string, number>;
  }
}

// 現在の合計人数を計算する（isIncludedInHeadcountLimit=trueのゲストタイプのみ）
const calculateTotalHeadCount = () => {
  if (!form.value.step1.guestCounts) {
    return 0;
  }

  let totalCount = 0;

  // 各ゲストタイプの人数を合計する（isIncludedInHeadcountLimitがtrueの場合のみ）
  props.experienceEvent.experienceEventFees?.forEach((fee) => {
    if (fee.guestType.isIncludedInHeadcountLimit && form.value.step1.guestCounts?.[fee.guestType.id]) {
      totalCount += form.value.step1.guestCounts[fee.guestType.id];
    }
  });

  return totalCount;
};

// 特定のゲストタイプの最大選択可能数を計算する
const calculateMaxCountForGuestType = (guestType: WabunkaGuestTypeViewModel) => {
  // isIncludedInHeadcountLimitがfalseの場合は、制限なし（100とする）
  if (!guestType.isIncludedInHeadcountLimit) {
    return 100;
  }

  const maxGuests = props.experienceEvent.maxGuests || 10; // デフォルト値として10を使用
  const currentTotalHeadCount = calculateTotalHeadCount();
  const currentGuestTypeCount = form.value.step1.guestCounts?.[guestType.id] || 0;

  // 現在の合計人数から、このゲストタイプの現在の人数を引いて、最大値との差を計算
  // これにより、他のゲストタイプの選択に影響を与えずに、このゲストタイプの最大値を計算できる
  return maxGuests - (currentTotalHeadCount - currentGuestTypeCount);
};

// ゲストタイプごとの人数を増やす
const incrementGuestCount = (guestTypeId: string) => {
  // form.value.step1.guestCounts が存在しない場合は初期化
  if (!form.value.step1.guestCounts) {
    form.value.step1.guestCounts = {} as Record<string, number>;
  }

  // 対応するゲストタイプオブジェクトを取得
  const guestTypeFee = props.experienceEvent.experienceEventFees?.find((fee) => fee.guestType.id === guestTypeId);
  if (!guestTypeFee) {
    return; // ゲストタイプが見つからない場合は何もしない
  }

  const currentCount = form.value.step1.guestCounts[guestTypeId] || 0;
  const maxCount = calculateMaxCountForGuestType(guestTypeFee.guestType);

  // 最大カウントを超える場合は増加させない
  if (currentCount >= maxCount) {
    return;
  }

  // watchでguestCountsの変更を検知するため、新しいオブジェクトとして作成
  form.value.step1.guestCounts = {
    ...form.value.step1.guestCounts,
    [guestTypeId]: currentCount + 1,
  };

  // 合計人数を計算して numOfParticipants に設定
  const totalHeadCount = calculateTotalHeadCount();
  form.value.step1.numOfParticipants = totalHeadCount;
};

// ゲストタイプごとの人数を減らす
const decrementGuestCount = (guestTypeId: string) => {
  // form.value.step1.guestCounts が存在しない場合は初期化
  if (!form.value.step1.guestCounts) {
    form.value.step1.guestCounts = {} as Record<string, number>;
  }

  const currentCount = form.value.step1.guestCounts[guestTypeId] || 0;
  if (currentCount > 0) {
    // watchでguestCountsの変更を検知するため、新しいオブジェクトとして作成
    form.value.step1.guestCounts = {
      ...form.value.step1.guestCounts,
      [guestTypeId]: currentCount - 1,
    };

    // 合計人数を計算して numOfParticipants に設定
    const totalHeadCount = calculateTotalHeadCount();
    form.value.step1.numOfParticipants = totalHeadCount;
  }
};

// デフォルト人数設定のヘルパー関数
const setDefaultGuestCounts = () => {
  const experienceEventFees = props.experienceEvent.experienceEventFees;
  if (!experienceEventFees?.length) return;

  // form.value.step1.guestCounts が存在しない場合は初期化
  if (!form.value.step1.guestCounts) {
    form.value.step1.guestCounts = {} as Record<string, number>;
  }

  // 既存の設定があるかチェック
  const hasAnyGuestCount = experienceEventFees.some((fee) => (form.value.step1.guestCounts?.[fee.guestType.id] ?? 0) > 0);

  // まだ何も設定されていない場合、デフォルト値を設定
  if (!hasAnyGuestCount) {
    // 最初のゲストタイプ（sequenceIdが最も小さいもの）を取得
    const firstGuestType = experienceEventFees[0];

    const newGuestCounts: Record<string, number> = {};
    experienceEventFees.forEach((fee) => {
      if (firstGuestType && firstGuestType.guestType.id === fee.guestType.id) {
        // 最も小さいsequenceIdのゲストタイプには最小人数を設定
        const defaultCount = props.experienceEvent.minGuestsPerReservation;
        newGuestCounts[fee.guestType.id] = defaultCount;
      } else {
        // その他は0
        newGuestCounts[fee.guestType.id] = 0;
      }
    });
    // watchでguestCountsの変更を検知するため、新しいオブジェクトとして一括設定
    form.value.step1.guestCounts = newGuestCounts;

    // 合計人数を更新
    const totalHeadCount = calculateTotalHeadCount();
    form.value.step1.numOfParticipants = totalHeadCount;
  }
};

watch(
  () => props.experienceEvent.id,
  (newId, oldId) => {
    if (newId !== oldId && newId) {
      setDefaultGuestCounts();
    }
  },
  { immediate: true },
);
</script>
